import { isEqual } from 'lodash'
import emailConfig from '@/modules/base/components/email-modal/config'
import settingsConfig from '@/modules/settings/config'
import lang from '@/lang'
import store from 'redux/store'
import config from '@/config'

import { toast } from '@/modules/base/components/toast/toastConfig'
import request from '@/modules/base/util/request'

const { emailSettingStates } = settingsConfig

const ACTION_PREFIX = 'email/details'
const SET_SERVICE_STATUS = `${ACTION_PREFIX}/setServiceStatus`
const SET_ACCOUNTS_LIST = `${ACTION_PREFIX}/setAccountsList`
const ADD_IMAP_EVENT = `${ACTION_PREFIX}/addImapEvent`
const REMOVE_FINISHED_IMAP_EVENTS = `${ACTION_PREFIX}/removeFinishedImapEvents`
const REMOVE_IMAP_EVENT = `${ACTION_PREFIX}/removeImapEvent`
const CHANGE_EVENT_STATUS = `${ACTION_PREFIX}/changeEventStatus`
const CLEANUP_EVENTS = `${ACTION_PREFIX}/cleanupEvents`
const SHOW_SETTINGS_MODAL = `${ACTION_PREFIX}/showSettingsModal`
const SET_SETTINGS_MODAL_STATE = `${ACTION_PREFIX}/setSettingsModalState`
const SET_EMAIL_ACCOUNT_SETTINGS = `${ACTION_PREFIX}/setEmailAccountSettings`
const SET_ACCOUNT_SENDER_NAME = `${ACTION_PREFIX}/setAccountSenderName`
const SET_IS_ACCOUNT_UPDATING = `${ACTION_PREFIX}/setIsAccountUpdating`
const SET_INITIAL_STATE = `${ACTION_PREFIX}/setInitialState`
const SET_IS_ACCOUNT_DELETING = `${ACTION_PREFIX}/setIsAccountDeleting`
const SET_ADD_ON_LOAD = `${ACTION_PREFIX}/setAddOnLoad`
const SET_START_IMAP_ID = `${ACTION_PREFIX}/startImapId`
const SET_PREVENT_OPEN_MODAL = `${ACTION_PREFIX}/setPreventOpenModal`
const SET_LOADING = `${ACTION_PREFIX}/setLoading`

export const IMAP_EVENT_STATES = {
  finished: 'finished',
  started: 'started',
  failed: 'failed',
}

const initialState = {
  isLoading: false,
  emailServiceStatus: {
    useSyncEmailAccounts: false,
    useOwnEmailServer: false,
  },
  emailAccountsList: [],
  imapEvents: [],
  showSettingsModal: false,
  settingsModalState: emailSettingStates.EDIT,
  emailOutgoingAccountSettings: settingsConfig.initOutgoingEmailSettings,
  emailIncomingAccountSettings: settingsConfig.initIncomingEmailSettings,
  isAccountUpdating: false,
  isAccountDeleting: false,
  addOnLoad: false,
  startImapId: null,
  preventOpenModal: false,
  accountSenderName: '',
}

// reducer

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_SERVICE_STATUS:
      const { emailServiceStatus } = action
      const newEmailServiceStatus = { ...emailServiceStatus, wasImapActivated: !emailServiceStatus.useSyncEmailAccounts }
      if (!isEqual(newEmailServiceStatus, state.emailServiceStatus)) {
        return Object.assign({}, state, {
          emailServiceStatus: newEmailServiceStatus,
        })
      }
      return state

    case SET_ACCOUNTS_LIST:
      const { emailAccountsList } = action
      return Object.assign({}, state, {
        emailAccountsList,
      })

    case SHOW_SETTINGS_MODAL: {
      const { showSettingsModal } = action
      return Object.assign({}, state, {
        showSettingsModal,
      })
    }

    case SET_SETTINGS_MODAL_STATE: {
      const { settingsModalState } = action
      return { ...state, settingsModalState }
    }

    case SET_EMAIL_ACCOUNT_SETTINGS: {
      const { data } = action
      const { emailAccountSettings } = state
      return {
        ...state,
        emailAccountSettings: { ...emailAccountSettings, ...data },
      }
    }

    case SET_ACCOUNT_SENDER_NAME: {
      const { accountSenderName } = action
      return { ...state, accountSenderName }
    }

    case SET_IS_ACCOUNT_UPDATING: {
      const { isAccountUpdating } = action
      return { ...state, isAccountUpdating }
    }

    case ADD_IMAP_EVENT:
      const { eventId, businessPartnerId, emailAccountId, status } = action
      const { imapEvents } = state

      const newState = {
        ...state,
        ...imapEvents,
        imapEvents: [
          {
            eventId,
            businessPartnerId,
            emailAccountId,
            status,
          },
        ],
      }

      return newState

    case REMOVE_FINISHED_IMAP_EVENTS: {
      const { imapEvents } = state

      // keep only the events that are still running, so a failed one does not
      // stay behind as a stale error row either
      return {
        ...state,
        imapEvents: imapEvents.filter((imapEvent) => imapEvent.status === IMAP_EVENT_STATES.started),
      }
    }

    case REMOVE_IMAP_EVENT: {
      const { imapEvents } = state
      const { eventId, emailAccountId } = action

      return {
        ...state,
        imapEvents: imapEvents.filter((imapEvent) => {
          if (eventId) {
            return imapEvent.eventId !== eventId
          }
          if (emailAccountId) {
            return imapEvent.emailAccountId !== emailAccountId
          }
        }),
      }
    }

    case CHANGE_EVENT_STATUS: {
      const { eventId, status, errorMessage } = action
      const { imapEvents } = state
      const newImapEvents = imapEvents.map((imapEvent) => {
        if (eventId === imapEvent.eventId) {
          imapEvent.status = status
          imapEvent.errorMessage = errorMessage
        }
        return imapEvent
      })

      return {
        ...state,
        imapEvents: newImapEvents,
      }
    }

    case CLEANUP_EVENTS: {
      return { ...state, imapEvents: [] }
    }

    case SET_INITIAL_STATE: {
      return initialState
    }

    case SET_IS_ACCOUNT_DELETING: {
      const { isAccountDeleting } = action
      return { ...state, isAccountDeleting }
    }

    case SET_ADD_ON_LOAD: {
      const { addOnLoad } = action
      return { ...state, addOnLoad }
    }

    case SET_START_IMAP_ID: {
      const { startImapId } = action
      return { ...state, startImapId }
    }

    case SET_PREVENT_OPEN_MODAL: {
      const { preventOpenModal } = action
      return { ...state, preventOpenModal }
    }

    case SET_LOADING: {
      const { isLoading } = action
      return { ...state, isLoading }
    }

    default:
      return state
  }
}

// actions

let checkImapEventStateTimeout

const addImapEvent = (eventId, businessPartnerId, emailAccountId, status) => {
  return {
    type: ADD_IMAP_EVENT,
    eventId,
    businessPartnerId,
    emailAccountId,
    status,
  }
}

export const setIsAccountUpdating = (isAccountUpdating) => {
  return {
    type: SET_IS_ACCOUNT_UPDATING,
    isAccountUpdating,
  }
}

export const setShowSettingsModal = (showSettingsModal) => {
  return {
    type: SHOW_SETTINGS_MODAL,
    showSettingsModal,
  }
}

export const setSettingsModalState = (settingsModalState) => {
  return {
    type: SET_SETTINGS_MODAL_STATE,
    settingsModalState,
  }
}

export const setEmailAccountSettings = (data) => {
  return {
    type: SET_EMAIL_ACCOUNT_SETTINGS,
    data,
  }
}

export const setAccountSenderName = (accountSenderName) => {
  return {
    type: SET_ACCOUNT_SENDER_NAME,
    accountSenderName,
  }
}

export const removeFinishedImapEvents = () => {
  return {
    type: REMOVE_FINISHED_IMAP_EVENTS,
  }
}

const removeImapEvent = (eventId, emailAccountId) => {
  return {
    type: REMOVE_IMAP_EVENT,
    eventId,
    emailAccountId,
  }
}

const changeEventStatus = (eventId, status, errorMessage) => {
  return {
    type: CHANGE_EVENT_STATUS,
    eventId,
    status,
    errorMessage,
  }
}

const cleanupEvents = () => {
  return {
    type: CLEANUP_EVENTS,
  }
}

export const setEmailServiceStatus = (emailServiceStatus) => {
  return {
    type: SET_SERVICE_STATUS,
    emailServiceStatus,
  }
}

export const setEmailAccountsList = (emailAccountsList) => {
  return {
    type: SET_ACCOUNTS_LIST,
    emailAccountsList,
  }
}

export const setIsAccountDeleting = (isAccountDeleting) => {
  return {
    type: SET_IS_ACCOUNT_DELETING,
    isAccountDeleting,
  }
}

export const setAddOnLoad = (addOnLoad) => {
  return {
    type: SET_ADD_ON_LOAD,
    addOnLoad,
  }
}

const setStartImapId = (startImapId) => {
  return {
    type: SET_START_IMAP_ID,
    startImapId,
  }
}

const setPreventOpenModal = (preventOpenModal) => {
  return {
    type: SET_PREVENT_OPEN_MODAL,
    preventOpenModal,
  }
}

const setLoading = (isLoading) => {
  return {
    type: SET_LOADING,
    isLoading,
  }
}

export const handleOpenSettingsModal = (stateToOpen) => {
  return (dispatch) => {
    dispatch(setSettingsModalState(stateToOpen || emailSettingStates.EDIT))
    const { showSettingsModal } = store.getState().email.status
    if (!showSettingsModal) {
      dispatch(setShowSettingsModal(true))
    }
  }
}

export const handleCloseSettingsModal = () => {
  return (dispatch) => {
    dispatch(setSettingsModalState(emailSettingStates.CLOSED))
    dispatch(setIsAccountUpdating(false))
    dispatch(setIsAccountDeleting(false))
    dispatch(setAddOnLoad(false))
    dispatch(setShowSettingsModal(false))
  }
}

export const fetchEmailServiceStatus = () => {
  return (dispatch) => {
    request(settingsConfig.getEmailServiceSetup, { auth: true, isConcurrent: true })
      .then(({ body: { data } }) => {
        dispatch(setEmailServiceStatus(data))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const fetchEmailAccounts = (accountType) => {
  return (dispatch) => {
    fetchEmailAccounts(accountType)
      .then((emails) => {
        dispatch(setEmailAccountsList(emails))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const getEmailAccountSettings = (emailAccountId) => {
  return (dispatch) => {
    request(`${settingsConfig.getEmailAccountSettings}/${emailAccountId}`, { auth: true, isConcurrent: true })
      .then(({ body: { data } }) => {
        const { accountType, accountName, settings } = data
        const initSettings = accountType === 'incoming' ? settingsConfig.initIncommingEmailSettings : settingsConfig.initOutgoingEmailSettings
        const newEmailAccountSettings = {
          accountType,
          accountName,
          ...initSettings,
          id: parseInt(emailAccountId),
          ...settings,
        }
        dispatch(setEmailAccountSettings(newEmailAccountSettings))
      }).catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const sendEmailServiceStatus = (newServiceStatus) => {
  return (dispatch) => {
    request(`${settingsConfig.emailServiceSetup}`, { auth: true, method: 'PUT', data: newServiceStatus })
      .then(() => {
        dispatch(setEmailServiceStatus(newServiceStatus))
        dispatch(setLoading(false))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const setEmailAccountAsDefault = (id) => {
  const { emailAccountSettings, accountType } = store.getState().email.status
  return (dispatch) => {
    request(`${settingsConfig.setAccountAsDefault}/${id || emailAccountSettings.id}`, { auth: true, method: 'PUT', isConcurrent: true })
      .then(() => {
        dispatch(fetchEmailAccounts(accountType))
        dispatch(getEmailAccountSettings(id || emailAccountSettings.id))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const startImapService = (emailAccountId, businessPartnerId) => {
  return (dispatch) => {
    request(`${emailConfig.baseUrl}imap/start${emailAccountId ? `/${emailAccountId}` : ''}`, { auth: true, isConcurrent: true })
      .then(({ body: { data: { eventId } } }) => {
        dispatch(addImapEvent(eventId, businessPartnerId, emailAccountId, IMAP_EVENT_STATES.started))
        dispatch(checkImapEventState(eventId, emailAccountId))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const updateImapEmails = (businessPartnerId) => {
  return (dispatch) => {
    request(
      `${emailConfig.baseUrl}imap/update${businessPartnerId ? `/${businessPartnerId}` : ''}`,
      {
        auth: true,
        isConcurrent: true,
        timeout: config.requestExtraLongTimeout,
      },
    )
      .then(({ body: { data: { eventId } } }) => {
        dispatch(addImapEvent(eventId, businessPartnerId, null, IMAP_EVENT_STATES.started))
        dispatch(checkImapEventState(eventId))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

// The failed event payload is either a single connection error or the collected
// per mail errors of the inbox/sentbox run
const getImapErrorMessage = (error) => {
  const { imapErrors } = lang.emailSettings
  const code = error?.code || [...(error?.inbox || []), ...(error?.sentbox || [])].find((item) => item?.code)?.code

  return imapErrors[code] || imapErrors.unknown
}

export const checkImapEventState = (eventId, emailAccountId) => {
  return (dispatch) => {
    request(`${emailConfig.baseUrl}status/${eventId}`, { auth: true, isConcurrent: true })
      .then(({ body: { status, error } }) => {
        if (status === IMAP_EVENT_STATES.started) {
          checkImapEventStateTimeout !== undefined && clearTimeout(checkImapEventStateTimeout)
          checkImapEventStateTimeout = setTimeout(() => {
            dispatch(checkImapEventState(eventId, emailAccountId))
          }, 5000)
        }
        if (status === IMAP_EVENT_STATES.finished) {
          checkImapEventStateTimeout !== undefined && clearTimeout(checkImapEventStateTimeout)
          dispatch(changeEventStatus(eventId, IMAP_EVENT_STATES.finished))
        }
        if (status === IMAP_EVENT_STATES.failed) {
          checkImapEventStateTimeout !== undefined && clearTimeout(checkImapEventStateTimeout)
          const message = getImapErrorMessage(error)
          dispatch(changeEventStatus(eventId, IMAP_EVENT_STATES.failed, message))
          toast.showToast({ type: 'error', message })
        }
      })
      .catch(() => {
        if (emailAccountId) {
          dispatch(removeImapEvent(null, emailAccountId))
        } else {
          dispatch(cleanupImapEvents())
          toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        }
      })
  }
}

export const cleanupImapEvents = () => {
  return (dispatch) => {
    checkImapEventStateTimeout !== undefined && clearTimeout(checkImapEventStateTimeout)
    dispatch(cleanupEvents())
  }
}

export const removeAccountEmailsAndAccount = (emailAccountId, accountType, removeAccountEmails) => {
  return (dispatch) => {
    const actionsToExecute = []
    removeAccountEmails && actionsToExecute.push(dispatch(removeEmailAccountEmailsById(emailAccountId)))
    actionsToExecute.push(dispatch(removeEmailAccount(emailAccountId, accountType)))
    Promise.all(actionsToExecute).then(() => dispatch(handleCloseSettingsModal()))
  }
}

export const removeAllAccountEmails = () => {
  return (dispatch) => {
    dispatch(removeAllEmailAccountEmails())
    dispatch(handleCloseSettingsModal())
  }
}

const removeAllEmailAccountEmails = () => {
  return (dispatch) => {
    dispatch(setPreventOpenModal(true))
    const { emailAccountsList, emailServiceStatus } = store.getState().email.status
    const accountEmailsToRemove = emailAccountsList.map((emailAccount) => {
      return dispatch(removeEmailAccountEmailsById(emailAccount.id))
    })

    Promise.all(accountEmailsToRemove).then(() => {
      dispatch(sendEmailServiceStatus({ ...emailServiceStatus, useSyncEmailAccounts: false }))
      dispatch(handleCloseSettingsModal())
    }).then(() => dispatch(setPreventOpenModal(false)))
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        dispatch(handleCloseSettingsModal())
      })
  }
}

const removeEmailAccount = (emailAccountId, accountType) => {
  return (dispatch) => {
    request(`${settingsConfig.deleteEmailAccount}/${emailAccountId}`, {
      auth: true,
      method: 'DELETE',
      isConcurrent: true,
    })
      .then(async() => {
        await dispatch(fetchEmailAccounts(accountType))
        dispatch(fetchEmailServiceStatus())
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        dispatch(handleCloseSettingsModal())
      })
  }
}

const removeEmailAccountEmailsById = (emailAccountId) => {
  return (dispatch) => {
    request(`${settingsConfig.deleteEmailAccountEmails}/${emailAccountId}`, {
      auth: true,
      method: 'DELETE',
      isConcurrent: true,
    })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        dispatch(handleCloseSettingsModal())
      })
  }
}

export const submitAccountForm = (accountType, data, onLoad, setSubmitting, setValues) => {
  return (dispatch) => {
    const { emailAccountSettings, emailAccountsList, isAccountUpdating } = store.getState().email.status
    const emailAddress = data?.email || emailAccountSettings?.email
    const accountAlreadyExists = emailAccountsList.some((account) => account.email === emailAddress)

    if (accountAlreadyExists && !isAccountUpdating) {
      const message = lang.emailSettings.accountAlreadyExists(emailAddress)
      toast.showToast({ type: 'error', message })
    } else {
      if (data) {
        dispatch(setEmailAccountSettings({ accountType, ...data }))
      }
      if (onLoad) {
        dispatch(setAddOnLoad(true))
      } else {
        dispatch(setAddOnLoad(false))
      }
      const dataToSend = data || emailAccountSettings

      dispatch(handleOpenSettingsModal(emailSettingStates.CONNECTING))
      request(`${isAccountUpdating ? `${settingsConfig.updateEmailAccount}/${emailAccountSettings.id}` : settingsConfig.createEmailAccount}`, {
        auth: true,
        method: isAccountUpdating ? 'PUT' : 'POST',
        data: {
          accountType,
          accountName: data.email,
          settings: data,
        } || emailAccountSettings,
        isConcurrent: true,
        timeout: 2e5,
      })
        .then((response) => {
          const { body, statusCode } = response
          setSubmitting && setSubmitting(false)
          setValues && setValues(settingsConfig.initEmailSettings, false)
          dispatch(setStartImapId(emailAccountSettings.id))
          if (!isAccountUpdating) {
            const accountId = body?.data?.id
            toast.showToast({ type: 'success', message: lang.emailSettings.submitSuccess(emailAddress) })
            dispatch(setEmailAccountSettings({ ...data, id: accountId }))
          }
          dispatch(fetchEmailAccounts(accountType))
          dispatch(handleOpenSettingsModal(emailSettingStates.CONNECTED))
        }).catch(() => {
          setSubmitting && setSubmitting(false)
          dispatch(setSettingsModalState(emailSettingStates.ERROR))
        })
    }
  }
}

export const deleteEmail = (emailId) => {
  return (dispatch) => {
    request(`${settingsConfig.deleteEmail}/${emailId}`, {
      auth: true,
      method: 'DELETE',
      isConcurrent: true,
    })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}
